import React, { useState, useContext, useEffect } from "react";
import styles from "./Header2.module.css";
import { useNavigate } from "react-router-dom";
import { CMSContext } from "../Context/CmsContext";

const Header2 = () => {
  const [menuOptions, setMenuOptions] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isHamburgerOpen, setIsHamburgerOpen] = useState(false);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState(null);
  const navigate = useNavigate();
  const { cmsData } = useContext(CMSContext);

  const toggleHamburger = () => {
    setIsHamburgerOpen((prevState) => !prevState);
  };

  const handleOptionClick = () => {
    setIsHamburgerOpen(false);
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    if (cmsData.length > 0) {
      groupCmsData(cmsData);
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [cmsData]);

  const toggleMobileDropdown = (label) => {
    setActiveMobileDropdown((prevLabel) =>
      prevLabel === label ? null : label
    );
  };

  const groupCmsData = (data) => {
    const filteredData = data.filter((item) => item.service !== null);

    const grouped = filteredData.reduce((acc, item) => {
      // normalize service key to capitalized form for menu matching
      const serviceKey =
        item.service.trim().toLowerCase() === "form" ? "Form" : item.service; // handle other special cases if needed

      if (!acc[serviceKey]) acc[serviceKey] = [];
      acc[serviceKey].push({
        name: item.title.trim(),
        path: `/${item.service
          .toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/\//g, "-")}/${item.title
          .trim()
          .replace(/\s+/g, "-")
          .replace(/\//g, "-")}/${item.id}`,
        service: item.service,
        title: item.title,
        id: item.id,
      });
      return acc;
    }, {});

    const personalBankingStructure = {
      label: "Personal Banking",
      dropdown: [
        {
          name: "Deposit Schemes",
          isCategory: false,
          hasSubItems: true,
          items: data
            .filter(
              (item) => item.service === "Personal Banking(Deposit Schemes)"
            )
            .map((item) => ({
              name: item.title,
              path: `/personal-banking/deposit-schemes/${item.title.replace(
                /\s+/g,
                "-"
              )}/${item.id}`,
              service: item.service,
              title: item.title,
              id: item.id,
            })),
        },
        {
          name: "Advances",
          isCategory: false,
          hasSubItems: true,
          items: data
            .filter((item) => item.service === "Personal Banking(Advances)")
            .map((item) => ({
              name: item.title,
              path: `/personal-banking/advances/${item.title.replace(
                /\s+/g,
                "-"
              )}/${item.id}`,
              service: item.service,
              title: item.title,
              id: item.id,
            })),
        },
      ],
    };

    const careerPage = data.find((item) => item.service === "Career");
    const serviceChargesPage = data.find(
      (item) => item.service === "Service Charges"
    );

    const dynamicMenuOptions = [
      { label: "Home", dropdown: [], path: "/" },
      { label: "About Us", dropdown: grouped["AboutUS"] || [] },
      { label: "Services", dropdown: grouped["Services"] || [] },
      {
        label: "Personal Banking",
        dropdown: personalBankingStructure.dropdown,
      },
      { label: "Form / Public Notices", dropdown: grouped["Form"] || [] },
      { label: "Interest", dropdown: grouped["Interest"] || [] },
      {
        label: "Service Charges",
        dropdown: [],
        path: serviceChargesPage
          ? `/service-charges/${serviceChargesPage.title.replace(
              /\s+/g,
              "-"
            )}/${serviceChargesPage.id}`
          : "/service-charges",
        service: "Service Charges",
        id: serviceChargesPage?.id,
      },
      {
        label: "Career",
        dropdown: [],
        path: careerPage
          ? `/career/${careerPage.title.replace(/\s+/g, "-")}/${careerPage.id}`
          : "/career",
        service: "Career",
        id: careerPage?.id,
      },
      { label: "Get In Touch", dropdown: [], path: "/getintouch" },
    ];

    setMenuOptions(dynamicMenuOptions);
  };

  const handleNavigation = (service, title, id, path, subcategory = null) => {
    if (path) {
      navigate(path);
    } else if (service && title && id) {
      const sanitizedTitle = title
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/\//g, "-");
      navigate(`/${service.toLowerCase()}/${sanitizedTitle}/${id}`);
    } else if (service && !title && !id) {
      navigate(`/${service.toLowerCase()}`);
    }
    handleOptionClick();
  };

  const handleMenuEnter = (label) => {
    setActiveDropdown(label);
    setIsHovered(true);
  };

  const handleMenuLeave = () => {
    setActiveDropdown(null);
    setIsHovered(false);
  };

  const handleDropdownEnter = () => {
    setIsHovered(true);
  };

  const handleDropdownLeave = () => {
    setActiveDropdown(null);
    setIsHovered(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prevState) => !prevState);
  };

  return (
    <header className={styles.header}>
      {isMobile && (
        <div
          className={`${styles.hamburger} ${
            isHamburgerOpen ? styles.open : ""
          }`}
          onClick={toggleMobileMenu}
        >
          <div className={`${styles.bar} ${styles.bar1}`}></div>
          <div className={`${styles.bar} ${styles.bar2}`}></div>
          <div className={`${styles.bar} ${styles.bar3}`}></div>
        </div>
      )}

      {isMobile && (
        <div
          className={`${styles.mobileMenu} ${
            isMobileMenuOpen ? styles.open : ""
          }`}
        >
          {menuOptions.map((menu) => (
            <div
              className={styles.navItem}
              key={menu.label}
              onClick={() => {
                if (menu.dropdown.length === 0) {
                  handleNavigation(
                    menu.service,
                    menu.title,
                    menu.id,
                    menu.path
                  );
                } else {
                  toggleMobileDropdown(menu.label);
                }
              }}
            >
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (menu.dropdown.length === 0) {
                    handleNavigation(
                      menu.service,
                      menu.title,
                      menu.id,
                      menu.path
                    );
                  }
                }}
              >
                {menu.label}
              </a>

              {menu.dropdown.length > 0 && (
                <div
                  className={`${styles.mobileDropdown} ${
                    activeMobileDropdown === menu.label
                      ? styles.openDropdown
                      : ""
                  }`}
                  onMouseEnter={handleDropdownEnter}
                  onMouseLeave={handleDropdownLeave}
                >
                  {menu.dropdown.map((item, index) => (
                    <div key={index} className={styles.dropdownItem}>
                      {item.hasSubItems ? (
                        <div className={styles.categoryContainer}>
                          <div
                            className={styles.categoryTitle}
                            onClick={(e) => {
                              e.stopPropagation();
                              const current = e.currentTarget.parentElement;
                              current.classList.toggle(styles.active);
                            }}
                          >
                            {item.name}
                          </div>
                          <div className={styles.subDropdown}>
                            {item.items.map((subItem, subIndex) => (
                              <a
                                key={subIndex}
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleNavigation(
                                    subItem.service,
                                    subItem.name,
                                    subItem.id,
                                    subItem.path
                                  );
                                }}
                                className={styles.subItem}
                              >
                                {subItem.name}
                              </a>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            handleNavigation(
                              item.service,
                              item.name,
                              item.id,
                              item.path
                            );
                          }}
                          className={styles.subItem}
                        >
                          {item.name}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {!isMobile && (
        <nav className={styles.nav}>
          {menuOptions.map((menu) => (
            <div
              className={styles.navItem}
              key={menu.label}
              onMouseEnter={() => handleMenuEnter(menu.label)}
              onMouseLeave={handleMenuLeave}
            >
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (menu.dropdown.length === 0) {
                    handleNavigation(
                      menu.service,
                      menu.title,
                      menu.id,
                      menu.path
                    );
                  }
                }}
              >
                {menu.label}
              </a>
              {menu.dropdown.length > 0 && (
                <>
                  <span className={styles.arrow}>&#9660;</span>
                  <div
                    className={`${styles.dropdown} ${
                      activeDropdown === menu.label ? styles.openDropdown : ""
                    }`}
                    onMouseEnter={handleDropdownEnter}
                    onMouseLeave={handleDropdownLeave}
                  >
                    <div
                      className={
                        menu.dropdown.length > 5
                          ? styles.dropdownScrollContainer
                          : undefined
                      }
                    >
                      {menu.dropdown.map((item, index) => (
                        <React.Fragment key={index}>
                          {item.hasSubItems ? (
                            <div className={styles.dropdownItem}>
                              <a
                                href="#"
                                onClick={(e) => e.preventDefault()}
                                className={styles.categoryTitle}
                              >
                                {item.name}
                              </a>
                              <div className={styles.subDropdown}>
                                {item.items.map((subItem, subIndex) => (
                                  <a
                                    key={subIndex}
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleNavigation(
                                        subItem.service,
                                        subItem.name,
                                        subItem.id,
                                        subItem.path
                                      );
                                    }}
                                    className={styles.subItem}
                                  >
                                    {subItem.name}
                                  </a>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handleNavigation(
                                  item.service,
                                  item.name,
                                  item.id,
                                  item.path
                                );
                              }}
                            >
                              {item.name}
                            </a>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </nav>
      )}
    </header>
  );
};

export default Header2;
